import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUsuarioLogado, isLoggedIn, getUsuarioSistemaId } from './auth';// Mapeamento de nomes de página para rotas/links do menu
import BASE_URL from './api';
// Chave = nome salvo no banco (PaginaSistema.nome)
// Valor = identificador usado no menu
const MAPEAMENTO_PAGINAS = {
  'Cliente': 'Cliente',
  'Fornecedor': 'Fornecedor',
  'Pessoas': 'Pessoas',
  'Agendamentos': 'Agendamentos',
  'Calendario': 'Calendario',
  'Oportunidade': 'Oportunidade',
  'Orcamento': 'Orcamento',
  'Contrato': 'Contrato',
  'Vendas': 'Vendas',
  'VendasProdutos': 'VendasProdutos',
  'ContasPagar': 'ContasPagar',
  'ContasReceber': 'ContasReceber',
  'NotaFiscal': 'NotaFiscal',
  'Producao': 'Producao',
  'ProjetoVisual': 'ProjetoVisual',
  'Tarefas': 'Tarefas',
  'MateriaPrima': 'MateriaPrima',
  'ServicosTerceiros': 'ServicosTerceiros',
  'Manutencao': 'Manutencao',
  'Garantia': 'Garantia',
  'RelacionamentoClientes': 'RelacionamentoClientes',
  'EntregasProdutos': 'EntregasProdutos',
  'EntregasServicos': 'EntregasServicos',
  'Prazos': 'Prazos',
  'Estoque': 'Estoque',
  'Produto': 'Produto',
  'Grupo': 'Grupo',
  'CadastrarUsuario': 'CadastrarUsuario',
  'GerenciarFuncionarios': 'GerenciarFuncionarios',
  'AditivoContratual': 'AditivoContratual',
};

const PermissoesContext = createContext({
  paginasPermitidas: [],
  loading: true,
  temPermissao: () => true,
  recarregarPermissoes: () => {},
});

export function PermissoesProvider({ children }) {
  const [paginasPermitidas, setPaginasPermitidas] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregarPermissoes = useCallback(async () => {
    // Se não está logado, libera tudo (ou bloqueia tudo, dependendo da lógica)
    if (!isLoggedIn()) {
      setPaginasPermitidas([]);
      setLoading(false);
      return;
    }

    const usuarioId = getUsuarioSistemaId();
    if (!usuarioId) {
      setPaginasPermitidas([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const headers = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${BASE_URL}/PaginaSistema/por-usuario/${usuarioId}`, { headers });
      
      if (response.ok) {
        const permissoes = await response.json();
        // Extrair nomes das páginas permitidas
        const nomes = permissoes.map(p => p.nome || p.Nome).filter(Boolean);
        console.log('✅ Permissões carregadas para usuário', usuarioId, ':', nomes);
        setPaginasPermitidas(nomes);
      } else {
        console.warn('⚠️ Erro ao carregar permissões:', response.status);
        // Se não conseguiu carregar, libera tudo para não bloquear o usuário
        setPaginasPermitidas(Object.keys(MAPEAMENTO_PAGINAS));
      }
    } catch (err) {
      console.error('❌ Erro ao carregar permissões:', err);
      // Em caso de erro, libera tudo para não bloquear o usuário
      setPaginasPermitidas(Object.keys(MAPEAMENTO_PAGINAS));
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar permissões quando o componente montar ou quando o login mudar
  useEffect(() => {
    carregarPermissoes();
    
    // Escutar mudanças no localStorage (login/logout)
    const handleStorageChange = () => {
      carregarPermissoes();
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [carregarPermissoes]);

  // Função para verificar se tem permissão para uma página específica
  const temPermissao = useCallback((nomePagina) => {
    // Se não está logado, libera tudo para mostrar o menu completo
    if (!isLoggedIn()) {
      return true;
    }
    
    // Se ainda está carregando, permite (para não flickar a UI)
    if (loading) {
      return true;
    }
    
    // Se não tem nenhuma permissão cadastrada, libera tudo (usuário admin ou sem restrições)
    if (paginasPermitidas.length === 0) {
      return true;
    }
    
    // Verificar se a página está na lista de permitidas
    return paginasPermitidas.includes(nomePagina);
  }, [paginasPermitidas, loading]);

  const value = {
    paginasPermitidas,
    loading,
    temPermissao,
    recarregarPermissoes: carregarPermissoes,
  };

  return (
    <PermissoesContext.Provider value={value}>
      {children}
    </PermissoesContext.Provider>
  );
}

// Hook para usar as permissões
export function usePermissoes() {
  const context = useContext(PermissoesContext);
  if (!context) {
    throw new Error('usePermissoes deve ser usado dentro de um PermissoesProvider');
  }
  return context;
}

export default PermissoesContext;
